import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, formatDateTime, formatUSD } from "@/lib/api";
import { JOB_STATUSES, PAYMENT_STATUSES, STATUS_STYLES, ui } from "@/lib/ui";

const COLS = ["Date & Time", "Customer", "Vehicle", "Route", "Miles", "Fee", "Status", "Payment", "Source"];
const SOURCE = { customer_request: ["Customer", "text-cyan-300"], staff_entry: ["Staff", "text-[#FF7AB8]"], csv_import: ["Import", "text-slate-400"] };

const StatusSelect = ({ job, field, options, onUpdated }) => {
  const change = async (value) => {
    try {
      onUpdated((await api.patch(`/jobs/${job.id}`, { [field]: value })).data);
      toast.success(`${field === "status" ? "Status" : "Payment"} → ${value}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Select value={job[field]} onValueChange={change}>
      <SelectTrigger className={`h-8 w-[130px] rounded-none font-mono text-xs uppercase border ${STATUS_STYLES[job[field]]}`} data-testid={`job-${field}-select-${job.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={ui.menu}>
        {options.map((o) => <SelectItem key={o} value={o} className="font-mono text-xs uppercase">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const JobsTable = ({ jobs, onUpdated }) => (
  <div className="border border-slate-800 bg-[#070B16]/90 overflow-x-auto">
    <Table data-testid="dispatch-jobs-table" className="min-w-[1100px]">
      <TableHeader>
        <TableRow className="border-slate-800 hover:bg-transparent">
          {COLS.map((c) => (
            <TableHead key={c} className={`font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-400/80 ${["Miles", "Fee"].includes(c) ? "text-right" : ""}`}>{c}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {jobs.map((j) => (
          <TableRow key={j.id} data-testid={`dispatch-job-row-${j.id}`} className="border-slate-800/80 hover:bg-cyan-500/[0.04] text-sm align-top">
            <TableCell className="whitespace-nowrap font-mono text-xs text-slate-300">{formatDateTime(j.date_time)}</TableCell>
            <TableCell className="text-slate-100">
              {j.customer_name || "—"}
              {j.customer_email && <span className="block font-mono text-[11px] text-slate-500">{j.customer_email}</span>}
              {j.driver_name && <span className="block font-mono text-[11px] text-slate-500">Driver: {j.driver_name}</span>}
            </TableCell>
            <TableCell><span className="font-mono text-xs text-slate-100 border border-slate-700 bg-slate-900 px-2 py-0.5">{j.vehicle_info || "—"}</span></TableCell>
            <TableCell className="text-slate-400 max-w-[260px]">{j.pickup_location} <span className="text-[#FF007F]">→</span> {j.dropoff_location}</TableCell>
            <TableCell className="text-right font-mono text-xs text-slate-400">{j.distance_miles != null ? j.distance_miles.toFixed(1) : "—"}</TableCell>
            <TableCell className="text-right font-mono font-bold text-[#00FF66] whitespace-nowrap">{formatUSD(j.towing_fee)}</TableCell>
            <TableCell><StatusSelect job={j} field="status" options={JOB_STATUSES} onUpdated={onUpdated} /></TableCell>
            <TableCell><StatusSelect job={j} field="payment_status" options={PAYMENT_STATUSES} onUpdated={onUpdated} /></TableCell>
            <TableCell className={`font-mono text-[11px] uppercase ${SOURCE[j.source][1]}`}>{SOURCE[j.source][0]}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
